import React from 'react';
import { Target, Heart, Award, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import LazyImage from '../components/LazyImage';

const About = () => {
  const values = [
    {
      icon: <Target className="w-10 h-10" />,
      title: "One-Stop Solution",
      description: "Complete swimming pool solutions including FRP, tile, concrete, liner pools with membranes, and complete filtration systems"
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: "Leading Manufacturer",
      description: "Premier manufacturer of diverse pool types from Olympic and infinity pools to residential and commercial installations"
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: "Cost-Effective Solutions",
      description: "Quality craftsmanship with innovative technology to deliver time-saving, simplified, and feasible products"
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: "Customer Growth",
      description: "Commitment to quality ensures continuous growth, fostering relationships where we grow together with our customers"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1731080647259-879ee314af81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2x8ZW58MHx8fHwxNzcwMDUyMTY5fDA&ixlib=rb-4.1.0&q=85"
            alt="About Us"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Aquarist</h1>
          <p className="text-xl md:text-2xl mb-2">Bengaluru's Premier Pool & Fitness Solutions Provider</p>
          <p className="text-lg text-aqua-300 font-semibold">Complete Pool & Spa Solutions</p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-navy-900 mb-6">
                Crafting Your Dream Swimming Experience
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  <strong>Established in 2019,</strong> Aquarist Pools and Fitness stands as a premier provider of complete swimming pool solutions. We pride ourselves on being the one-stop solution for various pool types, including FRP, tile, concrete, liner swimming pools with membranes, and complete filtration systems.
                </p>
                <p>
                  Our commitment is to emerge as the leading dealer for all swimming pool-related needs, from hot tubs to reinforced PVC membranes. Located in Electronic City, Bengaluru, we serve residential and commercial clients across the region with excellence.
                </p>
                <p>
                  <strong>From initial consultation to final handover and beyond,</strong> we're with you every step of the way, ensuring your pool or fitness installation exceeds expectations and transforms your space into an aquatic paradise.
                </p>
              </div>
            </div>
            <div className="relative">
              <LazyImage 
                src="https://images.pexels.com/photos/24461272/pexels-photo-24461272.jpeg"
                alt="Indoor luxury pool"
                className="rounded-lg shadow-2xl aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Offerings */}
      <section className="py-20 bg-gradient-to-b from-cream-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">Our Offerings</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are the leading manufacturer of swimming pools, offering innovative solutions for every need
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <LazyImage 
                src="https://images.unsplash.com/photo-1758448501014-3a6068fb9d51?w=800&q=80"
                alt="Modern pool design"
                className="rounded-lg shadow-2xl aspect-[4/3]"
              />
            </div>
            <div>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  Aquarist Pools is not just a provider; <strong>we are the leading manufacturer of swimming pools.</strong> Our expertise extends to a diverse range of innovative pools, catering to different needs such as:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-aqua-500 mt-1">•</span>
                    <span><strong>Olympic pools</strong> for competitive swimming</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-aqua-500 mt-1">•</span>
                    <span><strong>Infinity pools</strong> for luxury aesthetics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-aqua-500 mt-1">•</span>
                    <span><strong>Exercise pools</strong> for schools and colleges</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-aqua-500 mt-1">•</span>
                    <span><strong>Residential pools</strong> for private homes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-aqua-500 mt-1">•</span>
                    <span><strong>Hotels and resorts pools</strong> for commercial properties</span>
                  </li>
                </ul>
                <p className="pt-4">
                  We strive to turn your imaginative ideas into reality, crafting pools that are not only functional and efficient but also <strong>relaxing and breathtaking.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission and Commitment */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">Mission and Commitment</h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-aqua-50 to-white p-8 md:p-12 rounded-lg shadow-xl border border-aqua-100">
              <div className="space-y-6 text-lg text-gray-700">
                <p>
                  Our mission at Aquarist Pools is clear - <strong>to provide cost-effective swimming pool solutions.</strong> We aim to create enticing, stress-free swimming experiences for our customers.
                </p>
                <p>
                  Leveraging innovative technology, we develop <strong>time-saving, simplified, and feasible products</strong> that enhance the overall pool ownership experience. Our commitment to quality ensures continuous growth, fostering a relationship where we grow together with our customers.
                </p>
                <p className="text-xl font-semibold text-navy-900 pt-4">
                  At Aquarist Pools and Fitness, we don't just build pools; we create captivating spaces that bring dreams to life. Join us on this journey towards swimming excellence, where your imagination meets our expertise.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-white to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">Why Choose Aquarist</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-aqua-100 to-aqua-200 text-aqua-600 mb-6">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-navy-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Visit Our Office</h2>
            <p className="text-xl text-gray-300">
              Opposite to Accent Ashlar Apartment, Chikkanagamangala, 2, Huskur Rd<br />
              Electronic City, Bengaluru, Karnataka 560099
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 max-w-4xl mx-auto">
            <div className="aspect-video rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3890.2!2d77.6644!3d12.8463!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDUwJzQ2LjciTiA3N8KwMzknNTEuOCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Aquarist Pools Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
